import { AbsenceType } from '@lmt/shared-types';
import { SelectedResources } from '../../calendar/visit/common/types';
import { AbsenceCreatingFormData } from './types';
import * as yup from 'yup';
import i18next from 'i18next';
import { addMilliseconds, isValid } from 'date-fns';
import { DateTimeInterval } from '../../common/ui-kit/inputs/date-time/DateTimeIntervalPicker';
import { isEmpty } from '../../measurements/copy-datablock-field/common';

const requiredMessage = () => i18next.t('translation:errors.required');
const noResourcesMessage = () =>
  i18next.t('translation:absences.form.resources_warning_title');

function t(value: string, options?: any) {
  return i18next.t(`translation:errors.${value}` as any, options);
}

export const absenceYupSchema: yup.Schema<AbsenceCreatingFormData> = yup.object(
  {
    absenceType: yup.mixed<AbsenceType>().required(requiredMessage),
    title: yup
      .string()
      .trim()
      .when('absenceType', {
        is: (absenceType?: AbsenceType) => absenceType === AbsenceType.Holiday,
        then: (schema) => schema.required(requiredMessage),
      }),
    absenceReasonId: yup.number().when('absenceType', {
      is: (absenceType?: AbsenceType) =>
        absenceType === AbsenceType.ResourceUnavailability,
      then: (schema) => schema.required(requiredMessage),
      otherwise: (schema) => schema.nullable(),
    }),
    dateTimeInterval: yup.object().shape({
      startDate: yup
        .date()
        .nullable()
        .required(requiredMessage)
        .typeError(() => t('invalid_date')),
      startTimeInMills: yup
        .number()
        .nullable()
        .required(requiredMessage)
        .typeError(() => t('invalid_time')),
      endDate: yup
        .date()
        .nullable()
        .required(requiredMessage)
        .typeError(() => t('invalid_date'))
        .test(
          'start-less-than-end',
          () => t('end_date_later_than_start_date'),
          (_value, context) => validateDate(context.parent),
        ),
      endTimeInMills: yup
        .number()
        .nullable()
        .required(requiredMessage)
        .test('start-less-than-end', '   ', (_value, context) =>
          validateDate(context.parent),
        )
        .typeError(() => t('invalid_time')),
    }),
    resources: yup
      .mixed<SelectedResources>()
      .defined()
      .when('absenceType', {
        is: (absenceType?: AbsenceType) =>
          absenceType === AbsenceType.ResourceUnavailability,
        then: (schema) =>
          schema.test(
            'is-not-empty',
            noResourcesMessage,
            (value) =>
              value &&
              Object.values(value).filter((x) => !isEmpty(x)).length > 0,
          ),
      }),
    note: yup.string().defined(),
    acceptConflict: yup.boolean().defined(),
  },
);

function validateDate(interval: DateTimeInterval) {
  const startDate = interval.startDate;
  const startTimeInMills = interval.startTimeInMills;
  const endDate = interval.endDate;
  const endTimeInMills = interval.endTimeInMills;
  if (
    startDate &&
    endDate &&
    !isEmpty(startTimeInMills) &&
    !isEmpty(endTimeInMills) &&
    isValid(endDate) &&
    isValid(startDate) &&
    isValid(startTimeInMills) &&
    isValid(endTimeInMills)
  ) {
    const startDateTime = addMilliseconds(startDate, startTimeInMills!);
    const endDateTime = addMilliseconds(endDate, endTimeInMills!);
    return endDateTime > startDateTime;
  }
  return true;
}
